import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { kr, signedKr } from '../../lib/money';
import type { RouterOutputs } from '../../lib/trpc';
import { Panel } from '../common/Panel';

type Summary = RouterOutputs['period']['summary'];

/**
 * How the month adds up: income in, fixed costs and what was moved to
 * savings out, what's left to live on (the budget for everyday purchases),
 * and the period's result. Money carried over from earlier periods isn't part of this; the
 * balance-led number on Idag covers it.
 */
export function FlowCard({ summary }: { summary: Summary }) {
  const { income, fixedCosts, savings, variable, period } = summary;
  const fixedCount = fixedCosts.items.length;
  const awaitingIncome = income.planned === 0;

  return (
    <Panel title="Så går månaden ihop">
      <dl className="text-sm">
        <FlowRow
          label="Inkomst"
          hint={income.expected !== income.received ? `väntat ${kr(income.expected)}` : undefined}
          sub={income.other > 0 ? `varav övrigt ${kr(income.other)}` : undefined}
          value={kr(income.planned)}
        />
        <FlowRow
          sign="−"
          label="Fasta kostnader"
          hint={fixedCount > 0 ? `${fixedCount} st` : undefined}
          value={kr(fixedCosts.total)}
        />
        {savings.actual < 0 ? (
          // More was taken out of savings than put in: money back to live on
          <FlowRow sign="+" label="Taget från sparande" value={kr(-savings.actual)} />
        ) : (
          <FlowRow sign="−" label="Sparande" value={kr(savings.actual)} />
        )}
        <div className="mt-2 flex items-baseline justify-between gap-3 border-t-2 border-foreground/80 pt-3">
          <dt className="font-semibold">
            <span aria-hidden className="mr-1.5 inline-block w-3 text-muted-foreground">
              =
            </span>
            Att leva på
          </dt>
          <dd className="text-2xl font-semibold tracking-tight tabular-nums">
            {kr(variable.budget)}
          </dd>
        </div>
      </dl>

      {awaitingIncome ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Bara inkomst som har kommit in räknas, så summan växer när lönen landar.
        </p>
      ) : (
        <>
          <p className="mt-3 text-sm text-muted-foreground">
            <span className="tabular-nums">{kr(variable.spent)}</span>{' '}
            {period.isPast ? 'gick' : 'har gått'} till rörliga köp
            {variable.refunds > 0 && (
              <>
                {' '}
                efter avdrag för <span className="tabular-nums">{kr(variable.refunds)}</span> som
                kommit tillbaka
              </>
            )}
            .
          </p>
          <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-border pt-3 text-sm">
            <span className="font-medium">Periodens resultat</span>
            <span
              className={cn(
                'font-semibold tabular-nums',
                summary.leftToSpend < 0 && 'text-negative',
              )}
            >
              {signedKr(summary.leftToSpend)}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {period.isCurrent
              ? 'Inkomst minus allt som gått ut i perioden. Pengar kvar från förra perioden räknas inte här, de syns i Idag.'
              : 'Inkomst minus allt som gick ut i perioden.'}
          </p>
        </>
      )}
    </Panel>
  );
}

function FlowRow({
  sign,
  label,
  hint,
  sub,
  value,
}: {
  sign?: string;
  label: string;
  hint?: ReactNode;
  /** A muted second line under the label */
  sub?: ReactNode;
  value: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <dt className="min-w-0">
        <span aria-hidden className="mr-1.5 inline-block w-3 text-muted-foreground">
          {sign}
        </span>
        {label}
        {hint && <span className="ml-2 text-xs text-muted-foreground">{hint}</span>}
        {sub && <span className="ml-[1.125rem] block text-xs text-muted-foreground">{sub}</span>}
      </dt>
      <dd className="shrink-0 tabular-nums">{value}</dd>
    </div>
  );
}
